/**
 * Curated template library — Tier 1/2 artifacts from AILayout_Prototype
 * (single open hole, or a few named slots; no design-set / component-GLB
 * dependencies). See types.ts for provenance and the refresh command.
 */

import type { TemplateArtifact } from './types';

import singlePage from './library/single-page.template.json';
import poster from './library/poster.template.json';
import mosaic from './library/mosaic.template.json';
import widget from './library/widget.template.json';
import titledPage from './library/titled-page.template.json';
import dominantSidebar from './library/dominant-sidebar.template.json';
import wallPoster from './library/wall-poster.template.json';
import threeColumn from './library/3-column.template.json';
import threeColumnBalanced from './library/3-column-balanced.template.json';
import app from './library/app.template.json';
import cutoutMosaic from './library/cutout-mosaic.template.json';
import subjectBreakout from './library/subject-breakout.template.json';

// Presentation batch — the prototype's slide-* family (16:9 STRUCTURE
// templates with ai-fill slots) plus hero/title/stat/feature staples.
import slideCentered from './library/slide-centered.template.json';
import slideHeaderBody from './library/slide-header-body.template.json';
import slideHeaderColumns from './library/slide-header-columns.template.json';
import slideHeaderTwoColumns from './library/slide-header-two-columns.template.json';
import slideHeroCaption from './library/slide-hero-caption.template.json';
import slideHeroSupportRow from './library/slide-hero-support-row.template.json';
import slideBento from './library/slide-bento.template.json';
import slideTwoColumns from './library/slide-two-columns.template.json';
import slideThreeColumns from './library/slide-three-columns.template.json';
import slideTwoRows from './library/slide-two-rows.template.json';
import slideThreeRows from './library/slide-three-rows.template.json';
import slideQuadrants from './library/slide-quadrants.template.json';
import slideSidebarMain from './library/slide-sidebar-main.template.json';
import slideSplitStack from './library/slide-split-stack.template.json';
import slideSpotlightRail from './library/slide-spotlight-rail.template.json';
import slideFocalOverlay from './library/slide-focal-overlay.template.json';
import slideFullBleed from './library/slide-full-bleed.template.json';
import slideRowOfCells from './library/slide-row-of-cells.template.json';
import slideGrid from './library/slide-grid.template.json';
import heroCard from './library/hero-card.template.json';
import titleGeneral from './library/title-general.template.json';
import cardStat from './library/card-stat.template.json';
import cardFeature from './library/card-feature.template.json';
import bannerGeneral from './library/banner-general.template.json';
import editorialFeature from './library/editorial-feature.template.json';
import stepsHero from './library/steps-hero.template.json';
import productLaunch from './library/product-launch.template.json';

// Prototype reference set — the nine layouts in trace-sandbox#253, the visual
// benchmark for template/prototype parity. Spatial (arc/wheel/table) shapes
// and the two with fixed device geometry, which the flat slide-* family and
// the Tier 1/2 batch above never reached.
import triptychPage from './library/triptych-page.template.json';
import depthDeck from './library/depth-deck.template.json';
import pillCircleImage from './library/pill-circle-image.template.json';
import pillslidePillColumns from './library/pillslide-pill-columns.template.json';
import imageWheel from './library/image-wheel.template.json';
import museumExhibitionSemicircle from './library/museum-exhibition-semicircle.template.json';
import exhibitTableBench from './library/exhibit-table-bench.template.json';
import iphoneScreen from './library/iphone-screen.template.json';

// The prototype Home's Mosaic Row card: three ai-fill rows, no set geometry.
import mosaicRows from './library/mosaic-rows.template.json';

export const TEMPLATE_LIBRARY: readonly TemplateArtifact[] = [
    singlePage, poster, mosaic, widget, titledPage, dominantSidebar, wallPoster,
    threeColumn, threeColumnBalanced, app, cutoutMosaic, subjectBreakout,
    slideCentered, slideHeaderBody, slideHeaderColumns, slideHeaderTwoColumns,
    slideHeroCaption, slideHeroSupportRow, slideBento, slideTwoColumns,
    slideThreeColumns, slideTwoRows, slideThreeRows, slideQuadrants,
    slideSidebarMain, slideSplitStack, slideSpotlightRail, slideFocalOverlay,
    slideFullBleed, slideRowOfCells, slideGrid, heroCard, titleGeneral,
    cardStat, cardFeature, bannerGeneral, editorialFeature, stepsHero,
    productLaunch,
    triptychPage, depthDeck, pillCircleImage, pillslidePillColumns, imageWheel,
    museumExhibitionSemicircle, exhibitTableBench, iphoneScreen,
    mosaicRows,
] as unknown as TemplateArtifact[];

export function getTemplate(slug: string): TemplateArtifact | undefined {
    return TEMPLATE_LIBRARY.find(t => t.slug === slug);
}

/**
 * Keyword filter over slug, name, description, and category tags.
 * Empty/omitted query returns the full library (it is small).
 */
export function searchTemplates(query?: string): readonly TemplateArtifact[] {
    const q = query?.trim().toLowerCase();
    if (!q) return TEMPLATE_LIBRARY;
    const terms = q.split(/\s+/);
    return TEMPLATE_LIBRARY.filter(t => {
        const haystack = [
            t.slug, t.name, t.description ?? '',
            t.categoryTags?.when ?? '',
            ...(t.categoryTags?.keywords ?? []),
            ...(t.categoryTags?.purpose ?? []),
        ].join(' ').toLowerCase();
        return terms.some(term => haystack.includes(term));
    });
}
